import React, { Component } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Button, COLOR } from '@dized/ui';

// Local storage key for privacy policy acceptance
const PRIVACY_POLICY_KEY = 'dized-privacy-policy-accepted';
const PRIVACY_POLICY_VERSION = '1.0'; // Increment this when policy changes

// Overlay that grays out the entire page
const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(3px);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

// Modal container
const Modal = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  max-width: 600px;
  width: 100%;
  max-height: 80vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

// Header section
const Header = styled.div`
  background: linear-gradient(135deg, ${COLOR.TURQUOISE} 0%, #357abd 100%);
  color: white;
  padding: 24px;
  text-align: center;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 24px;
  font-weight: 600;
`;

// Content section
const Content = styled.div`
  padding: 24px;
  overflow-y: auto;
  flex: 1;
`;

const PolicyText = styled.div`
  line-height: 1.6;
  color: #333;
  
  h3 {
    color: ${COLOR.TURQUOISE};
    margin-top: 24px;
    margin-bottom: 12px;
    font-size: 18px;
  }
  
  p {
    margin-bottom: 16px;
  }
  
  ul {
    margin-bottom: 16px;
    padding-left: 20px;
  }
  
  li {
    margin-bottom: 8px;
  }
`;

// Footer with buttons
const Footer = styled.div`
  padding: 24px;
  background: #f8f9fa;
  border-top: 1px solid #e9ecef;
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

const AcceptButton = styled(Button)`
  background: ${COLOR.TURQUOISE};
  color: white;
  padding: 12px 24px;
  font-weight: 600;
  
  &:hover {
    background: #2aa0a8;
  }
`;

const PolicyButton = styled(Button)`
  background: transparent;
  color: ${COLOR.TURQUOISE};
  border: 1px solid ${COLOR.TURQUOISE};
  padding: 12px 24px;
  
  &:hover {
    background: ${COLOR.TURQUOISE};
    color: white;
  }
`;

class PrivacyPolicy extends Component {
  static propTypes = {
    onAccept: PropTypes.func,
  };

  static defaultProps = {
    onAccept: () => {},
  };

  constructor(props) {
    super(props);
    this.state = {
      isVisible: false,
    };
  }

  componentDidMount() {
    // Check if privacy policy has been accepted
    const accepted = this.getPrivacyAcceptance();
    if (!accepted) {
      this.setState({ isVisible: true });
    }
  }

  getPrivacyAcceptance = () => {
    try {
      const stored = localStorage.getItem(PRIVACY_POLICY_KEY);
      if (stored) {
        const data = JSON.parse(stored);
        return data.version === PRIVACY_POLICY_VERSION && data.accepted;
      }
    } catch (error) {
      console.warn('Error reading privacy policy acceptance from localStorage:', error);
      // Fallback to cookies
      return this.getCookieValue(PRIVACY_POLICY_KEY) === PRIVACY_POLICY_VERSION;
    }
    return false;
  };

  setPrivacyAcceptance = (accepted) => {
    const data = {
      accepted,
      version: PRIVACY_POLICY_VERSION,
      timestamp: new Date().toISOString(),
    };

    try {
      localStorage.setItem(PRIVACY_POLICY_KEY, JSON.stringify(data));
    } catch (error) {
      console.warn('Error saving to localStorage, falling back to cookies:', error);
    }
    
    // Always set cookies as backup and for cross-subdomain access
    this.setDomainCookies(PRIVACY_POLICY_KEY, PRIVACY_POLICY_VERSION);
  };

  getDomainFromUrl = () => {
    const hostname = window.location.hostname;
    
    // Check if we're on localhost or development environment
    if (hostname === 'localhost' || hostname.startsWith('192.168.') || hostname.startsWith('127.0.')) {
      return hostname;
    }
    
    // For production, extract the root domain (e.g., "dized.com" from "app.dized.com")
    const parts = hostname.split('.');
    if (parts.length >= 2) {
      return `.${parts.slice(-2).join('.')}`;
    }
    
    return hostname;
  };

  setDomainCookies = (name, value) => {
    const currentDomain = this.getDomainFromUrl();
    const expires = new Date();
    expires.setTime(expires.getTime() + (365 * 24 * 60 * 60 * 1000)); // 1 year
    
    const cookieOptions = [
      `${name}=${value}`,
      `expires=${expires.toUTCString()}`,
      'path=/',
      'SameSite=Lax'
    ];

    // Set cookie for current domain
    document.cookie = cookieOptions.join(';');
    
    // If we're not on localhost and the domain looks like it could be a subdomain,
    // also set a wildcard domain cookie
    if (!currentDomain.includes('localhost') && !currentDomain.includes('192.168.') && !currentDomain.includes('127.0.')) {
      const wildcardCookieOptions = [
        `${name}=${value}`,
        `expires=${expires.toUTCString()}`,
        'path=/',
        `domain=${currentDomain}`,
        'SameSite=Lax'
      ];
      document.cookie = wildcardCookieOptions.join(';');
      
      // Also set without Secure flag for HTTP compatibility
      document.cookie = wildcardCookieOptions.join(';');
      
      // Set with Secure flag for HTTPS compatibility if we're on HTTPS
      if (window.location.protocol === 'https:') {
        wildcardCookieOptions.push('Secure');
        document.cookie = wildcardCookieOptions.join(';');
      }
    }
  };

  getCookieValue = (name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
  };

  setCookie = (name, value, days) => {
    const expires = new Date();
    expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
    document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/`;
  };

  handleAccept = () => {
    this.setPrivacyAcceptance(true);
    this.setState({ isVisible: false });
    this.props.onAccept();
  };

  openPrivacyPolicy = () => {
    window.open('https://dized.com/privacy-policy/', '_blank');
  };

  render() {
    const { isVisible } = this.state;

    if (!isVisible) {
      return null;
    }

    return (
      <Overlay>
        <Modal>
          <Header>
            <Title>Privacy Policy & Data Usage</Title>
          </Header>
          
          <Content>
            <PolicyText>
              <p>
                Welcome to Dized! We value your privacy and want to be transparent about how we collect, 
                use, and protect your personal information. Please read this policy carefully.
              </p>

              <h3>Information We Collect</h3>
              <ul>
                <li>Account information (email, username, profile data)</li>
                <li>Game and project data you create or upload</li>
                <li>Usage analytics to improve our services</li>
                <li>Technical information (browser type, IP address, device information)</li>
              </ul>

              <h3>How We Use Your Information</h3>
              <ul>
                <li>Provide and improve our game development tools</li>
                <li>Facilitate collaboration between team members</li>
                <li>Send important service updates and notifications</li>
                <li>Analyze usage patterns to enhance user experience</li>
              </ul>

              <h3>Cookies and Local Storage</h3>
              <p>
                We use cookies and local storage to:
              </p>
              <ul>
                <li>Keep you logged in and remember your preferences</li>
                <li>Store your privacy policy acceptance</li>
                <li>Analyze site usage and performance</li>
                <li>Provide personalized content and features</li>
              </ul>

              <h3>Data Protection</h3>
              <p>
                We implement industry-standard security measures to protect your data. 
                Your information is encrypted in transit and at rest, and we regularly 
                audit our security practices.
              </p>

              <h3>Your Rights</h3>
              <p>
                You have the right to access, update, or delete your personal information. 
                You can also opt out of non-essential data collection by contacting our support team.
              </p>

              <p>
                For the complete privacy policy, please visit{' '}
                <a 
                  href="https://dized.com/privacy-policy/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ color: COLOR.TURQUOISE, textDecoration: 'none' }}
                >
                  our website
                </a>.
              </p>
            </PolicyText>
          </Content>

          <Footer>
            <PolicyButton onClick={this.openPrivacyPolicy}>
              Privacy Policy
            </PolicyButton>
            <AcceptButton onClick={this.handleAccept}>
              Accept & Continue
            </AcceptButton>
          </Footer>
        </Modal>
      </Overlay>
    );
  }
}

export default PrivacyPolicy;
